import React, { FC, Fragment, useState } from 'react';

import cx from 'classnames';
import { Arrow, ToggleLayer } from 'react-laag';

import Dropdown from 'components/shared/Dropdown';
import Icon from 'components/shared/Icon';
import Tooltip, { TooltipText, VARIANTS } from 'components/shared/Tooltip';
import Text from 'components/shared/Typography/Text';

import s from './index.module.css';
import { IDetailsActionMenuProps, ICoords, IAction } from './types';

const ActionsMenu: FC<IDetailsActionMenuProps> = ({
  actions = [],
  children,
  className,
  optionsClassName,
  marginTop = '8px',
  noArrow = false,
  isOpenInClickPlace = false,
  customLayerStyles,
  childrenWrapperClassName,
  disabled,
  menuRef,
}) => {
  const [coords, setCoords] = useState<ICoords>({ x: 0, y: 0 });

  const Action = ({ icon, title, onClick, isDisabled, tooltipText, onClose }: IAction & { onClose: () => void }) => (
    <Fragment key={title}>
      <button
        key={title}
        className={cx(s.optionWrapper, s.option, optionsClassName, isDisabled && s.disabled)}
        disabled={isDisabled}
        onClick={() => {
          onClick();
          onClose();
        }}
      >
        <Icon className={s.icon} component={icon} />
        <Text accent className={s.text} data-tip data-for={'tooltip'}>
          {title}
        </Text>
      </button>
      <Tooltip
        variant={VARIANTS.CUSTOM}
        overridePosition={() => ({
          left: 26,
          top: -25,
        })}
        className={s.tooltip}
        id="tooltip"
        disable={!isDisabled || !tooltipText}
        arrowColor="#424242"
        backgroundColor="#424242"
        textColor="var(--tooltip-bg-color)"
      >
        <TooltipText className={s.tooltipText}>{tooltipText}</TooltipText>
      </Tooltip>
    </Fragment>
  );

  return (
    <ToggleLayer
      fixed
      isOpen={disabled ? false : undefined}
      renderLayer={({ isOpen, layerProps, arrowStyle, layerSide, close }) => {
        // 15 is a usual offset, 300 is a height of the menu
        const offsetY = layerSide === 'bottom' ? 15 : -315;
        const positions = isOpenInClickPlace
          ? { top: coords.y + offsetY, left: layerProps.style.left }
          : { top: layerProps.style.top, left: layerProps.style.left };

        return (
          isOpen && (
            <div
              ref={(node) => {
                layerProps.ref(node);
                if (menuRef) {
                  (menuRef as any).current = node;
                }
              }}
              className="layer"
              style={{
                ...layerProps.style,
                ...positions,
                zIndex: 9998,
                marginTop,
                ...customLayerStyles,
              }}
            >
              <Dropdown className={cx(s.wrapper, className)}>
                {!noArrow && (
                  <Arrow
                    style={{ ...arrowStyle, height: '1.1rem', zIndex: '21' }}
                    layerSide={layerSide}
                    borderWidth={1}
                    borderColor="rgba(0, 0, 0, 0.04)"
                    backgroundColor="#fff"
                    angle={45}
                    roundness={0.5}
                    size={12}
                  />
                )}
                {actions.map((actionOrGroup, i) =>
                  Array.isArray(actionOrGroup) ? (
                    <>
                      <div className={s.group}>
                        {actionOrGroup.map((action) => (
                          <Action key={action.id} {...action} onClose={close} />
                        ))}
                      </div>
                      {i !== actions.length - 1 && <div className={s.divider} />}
                    </>
                  ) : (
                    <Action key={i} {...actionOrGroup} onClose={close} />
                  ),
                )}
              </Dropdown>
            </div>
          )
        );
      }}
      placement={{
        anchor: 'BOTTOM_CENTER',
        autoAdjust: true,
        triggerOffset: 10,
        possibleAnchors: ['TOP_CENTER'],
      }}
      closeOnOutsideClick
    >
      {({ triggerRef, toggle }) => {
        return (
          <div
            ref={triggerRef}
            onClick={(e) => {
              setCoords({ x: e.clientX, y: e.clientY });
              if (!disabled) toggle();
            }}
            className={childrenWrapperClassName}
          >
            {children}
          </div>
        );
      }}
    </ToggleLayer>
  );
};

export default ActionsMenu;
